import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface CarouselImage {
  src: string;
  alt: string;
  title: string;
  description: string;
}

const HeroCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Church photos from assets folder
  const images: CarouselImage[] = [
    {
      src: require('../assets/altar+stained-glass-l.jpg'),
      alt: 'All Saints Catholic Church altar with beautiful stained glass windows',
      title: 'Welcome to All Saints Catholic Church',
      description: 'A welcoming community where faith, hope, and love flourish together'
    },
    {
      src: require('../assets/front-church.jpg'),
      alt: 'Front view of All Saints Catholic Church, Thirsk',
      title: 'Join Our Community',
      description: 'Experience the warmth of our church family and grow in faith together'
    },
    {
      src: require('../assets/pews-l.jpg'),
      alt: 'Interior view of All Saints Catholic Church with wooden pews',
      title: 'Come and Worship',
      description: 'Join us for Mass and experience the peace and joy of worship'
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, images.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  return (
    <section
      className="relative h-96 md:h-[500px] overflow-hidden"
      aria-label="Hero image carousel"
    >
      {/* Carousel Images */}
      <div className="relative h-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden={index !== currentSlide}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center text-white px-4 max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4">
            {images[currentSlide].title}
          </h2>
          <p className="text-lg md:text-xl mb-8 text-gray-200">
            {images[currentSlide].description}
          </p>
          <div className="space-x-4">
            <button 
              onClick={() => window.open('https://www.google.com/maps/place/All+Saints+Catholic+Church/@54.23153,-1.3468156,549m/data=!3m2!1e3!4b1!4m5!3m4!1s0x487eb74739d9144b:0xcc8280275af43288!8m2!3d54.23153!4d-1.3446269?hl=en', '_blank')}
              className="bg-church-gold hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-gold focus:ring-offset-2"
            >
              Plan Your Visit
            </button>
            <button 
              onClick={() => window.open('https://www.youtube.com/@AllSaintsChurchThirsk', '_blank')}
              className="border-2 border-white text-white hover:bg-white hover:text-church-blue font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-church-blue"
            >
              Watch Live
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-church-gold"
        aria-label="Previous image"
      >
        <ChevronLeftIcon className="h-6 w-6" />
      </button>      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-church-gold"
        aria-label="Next image"
      >
        <ChevronRightIcon className="h-6 w-6" />
      </button>      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-gold ${
              index === currentSlide ? 'bg-church-gold' : 'bg-white bg-opacity-50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Play/Pause Control */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white px-3 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-church-gold"
          aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
        >
          {isPlaying ? 'Pause' : 'Play'}
        </button>
      </div>
    </section>
  );
};

export default HeroCarousel;